import { noop } from "lodash";
import { memo, useMemo } from "react";

import {
  CostCenterForm,
  type FormValues as CostCenterFormValues,
} from "@/components/shared/form/lineItems/cost-center";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";

export enum SpecialFilterValue {
  Unknown = "unknown",
}

type Props = {
  lineItemIds: string[];
  costCenterIds: (string | null)[];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onChange: (value: CostCenterFormValues) => Promise<void> | void;
  onSuccess?: (value: string | null) => void;
  onError?: (error: Error) => void;
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
};

export const CostCenterCell = memo(
  ({
    readOnly = false,
    disabled = false,
    lineItemIds,
    costCenterIds,
    onChange = noop,
    onError = noop,
    onSuccess = noop,
    className,
  }: Props) => {
    const { costCenters } = useGetAllCostCenters();

    const currentCostCenters = useMemo(() => {
      if (!costCenters) return [];

      return costCenterIds.map((costCenterId) =>
        costCenters.find((a) => a.costCenterId === costCenterId),
      );
    }, [costCenterIds, costCenters]);

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="w-full">
            <CostCenterForm
              readOnly={readOnly}
              initialValues={{
                costCenterId: currentCostCenters[0]?.costCenterId ?? null,
                lineItemIds,
              }}
              costCenters={costCenters ?? []}
              onSubmit={onChange}
              onError={onError}
              onSuccess={onSuccess}
              fullWidth={false}
              variant="ghost"
              className={className}
              disabled={disabled}
            />
          </TooltipTrigger>
          <TooltipContent>{currentCostCenters[0]?.name}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
